/**
 * Small SVG circular progress ring.
 *
 * Renders a light track with a coloured arc whose length encodes a 0..1
 * fraction, starting at the 12 o'clock position and filling clockwise. Used on
 * the "Medical Necessity" action to show how many guidelines are fully
 * reviewed.
 * @module CircularProgressRing
 */
import React from "react";

/** Props for {@link CircularProgressRing}. */
interface CircularProgressRingProps {
  /** Progress fraction in the range 0..1 (values outside are clamped). */
  fraction: number;
  /** Outer diameter in pixels. */
  size?: number;
  /** Ring stroke width in pixels. */
  strokeWidth?: number;
  /** Colour of the filled progress arc. */
  color?: string;
  /** Colour of the unfilled track. */
  trackColor?: string;
}

/**
 * Renders a circular progress ring for the given fraction.
 *
 * @param props - Fraction plus optional size, stroke, and colour overrides
 * @returns The rendered SVG ring
 */
export const CircularProgressRing: React.FC<CircularProgressRingProps> = ({
  fraction,
  size = 18,
  strokeWidth = 2.5,
  color = "#0056D6",
  trackColor = "#E6E6E6",
}) => {
  const clamped = Math.min(Math.max(fraction, 0), 1);
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const filledLength = circumference * clamped;
  const percentLabel = `${Math.round(clamped * 100)}% reviewed`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
      role="img"
      aria-label={percentLabel}
    >
      <title>{percentLabel}</title>
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={strokeWidth}
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={`${filledLength} ${circumference}`}
        transform={`rotate(-90 ${center} ${center})`}
      />
    </svg>
  );
};
